import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService, CurrentUser } from '../../services/auth.service';
import { TenantService } from '../../services/tenant.service';
import { AppContext, ContextService } from '../../services/context.service';
import { NotificationStore } from '../../core/notifications/notification.store';
import { UserNotificationItem } from '../../core/notifications/notification.models';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-tenant-shell',
  standalone: true,
    imports: [CommonModule, RouterModule, TranslateModule],
    templateUrl: './tenant-shell.component.html',
    styleUrls: ['./tenant-shell.component.scss']
})
export class TenantShellComponent implements OnInit, OnDestroy {
  readonly appVersion = environment.appVersion;
  @ViewChild('notificationsMenuHost') notificationsMenuHost?: ElementRef<HTMLElement>;

  tenantLabel: string | null = null;
  showBackButton = false;
  backRoute = '/dashboard';
  breadcrumbs: Array<{ labelKey: string; url?: string }> = [];
  availableContexts: AppContext[] = [];
  currentContext: AppContext = 'admin';
  currentUser: CurrentUser | null = null;
  isNotificationsMenuOpen = false;
  isNotificationsRoute = false;
  unreadPreviewItems: UserNotificationItem[] = [];
  isLoadingUnreadPreview = false;
  unreadPreviewError: string | null = null;
  readonly unreadPreviewLimit = 6;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(AuthService) private authService: AuthService,
    @Inject(TenantService) private tenantService: TenantService,
    @Inject(ContextService) private contextService: ContextService,
    @Inject(Router) private router: Router,
    @Inject(NotificationStore) protected readonly notificationStore: NotificationStore,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isNotificationsMenuOpen) {
      return;
    }

    const target = event.target;
    const host = this.notificationsMenuHost?.nativeElement;
    if (!(target instanceof Node) || !host) {
      return;
    }

    if (!host.contains(target)) {
      this.closeNotificationsMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeNotificationsMenu();
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.authService.ensureCurrentUserLoaded();
    this.notificationStore.refreshUnreadCount().catch(() => undefined);
    this.currentContext = this.contextService.getCurrentContext();
    const tenant = this.tenantService.getCurrentTenantSync();
    this.tenantLabel = tenant?.name || tenant?.schema_name || null;

    this.contextService.context$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.currentContext = context;
      });

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
        const contexts = this.getContextsFromRoles(user?.roles || []);
        this.availableContexts = contexts;
        if (!contexts.includes(this.currentContext)) {
          this.currentContext = contexts[0] || 'admin';
          this.contextService.setContext(this.currentContext);
        }
      });

    this.updateNavState(this.router.url);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.closeNotificationsMenu();
        this.updateNavState((event as NavigationEnd).urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  getUserDisplayName(): string {
    if (!this.currentUser) {
      return '';
    }
    const fullName = [this.currentUser.first_name, this.currentUser.last_name]
      .filter(Boolean)
      .join(' ');
    return fullName || this.currentUser.email;
  }

  onContextChange(context: string): void {
    if (this.isAppContext(context)) {
      this.currentContext = context;
      this.contextService.setContext(context);
    }
  }

  getContextLabelKey(context: AppContext): string {
    const map: Record<AppContext, string> = {
      admin: 'pages.dashboard.context.admin',
      coach: 'pages.dashboard.context.coach',
      parent: 'pages.dashboard.context.parent',
      player: 'pages.dashboard.context.player'
    };
    return map[context];
  }

  async onNotificationsTriggerClick(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (this.notificationStore.unreadCount() <= 0) {
      await this.openNotificationsCenter();
      return;
    }

    if (this.isNotificationsMenuOpen) {
      this.closeNotificationsMenu();
      return;
    }

    this.isNotificationsMenuOpen = true;
    await this.loadUnreadPreview();

    if (!this.unreadPreviewError && this.unreadPreviewItems.length === 0) {
      this.closeNotificationsMenu();
      await this.notificationStore.refreshUnreadCount(true);
      await this.openNotificationsCenter();
    }
  }

  async openNotificationsCenter(event?: MouseEvent): Promise<void> {
    event?.stopPropagation();
    this.closeNotificationsMenu();
    await this.router.navigate(['/notifications']);
  }

  closeNotificationsMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.isNotificationsMenuOpen = false;
  }

  resolveUnreadPreviewTitle(item: UserNotificationItem): string {
    return item.title_pl || item.title_en || item.title_de;
  }

  async refreshUnreadPreview(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    await this.loadUnreadPreview();
  }

  isNotificationsTriggerActive(): boolean {
    return this.isNotificationsRoute || this.isNotificationsMenuOpen;
  }

  private async loadUnreadPreview(): Promise<void> {
    this.isLoadingUnreadPreview = true;
    this.unreadPreviewError = null;

    try {
      const accessToken = this.authService.getAccessToken();
      const requestUrl = new URL(`${this.tenantService.getApiBaseUrl()}/api/notifications/`);
      requestUrl.searchParams.set('page', '1');
      requestUrl.searchParams.set('page_size', String(this.unreadPreviewLimit));
      requestUrl.searchParams.set('unread', 'true');

      const response = await fetch(requestUrl.toString(), {
        method: 'GET',
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('unread-preview-http-error');
      }

      const payload = (await response.json()) as { results?: UserNotificationItem[] };
      this.unreadPreviewItems = (payload.results ?? []).slice(0, this.unreadPreviewLimit);
    } catch {
      this.unreadPreviewItems = [];
      this.unreadPreviewError = 'common.errors.failed_load';
    } finally {
      this.isLoadingUnreadPreview = false;
    }
  }

  private getContextsFromRoles(roles: string[]): AppContext[] {
    const roleMap: Record<string, AppContext> = {
      'Admins': 'admin',
      'Super Admins': 'admin',
      'Coaches': 'coach',
      'Parents': 'parent',
      'Players': 'player'
    };

    const contexts = roles
      .map((role) => roleMap[role])
      .filter((context): context is AppContext => Boolean(context));

    return Array.from(new Set(contexts));
  }

  private isAppContext(value: string): value is AppContext {
    return ['admin', 'coach', 'parent', 'player'].includes(value);
  }

  private updateNavState(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    this.isNotificationsRoute = cleanUrl === '/notifications' || cleanUrl.startsWith('/notifications/');

    if (cleanUrl.startsWith('/notifications')) {
      let notificationsLabelKey = 'pages.notifications.list.title';
      if (cleanUrl === '/notifications/preferences') {
        notificationsLabelKey = 'pages.notifications.preferences.title';
      } else if (cleanUrl !== '/notifications') {
        notificationsLabelKey = 'pages.notifications.detail.title';
      }

      this.backRoute = '/dashboard';
      this.showBackButton = cleanUrl !== '/dashboard';
      this.breadcrumbs = [
        {
          labelKey: 'pages.admin.dashboard.title',
          url: '/dashboard'
        },
        {
          labelKey: notificationsLabelKey
        }
      ];
      return;
    }

    if (cleanUrl.startsWith('/admin')) {
      const adminLabelMap: Record<string, string> = {
        '/admin/users': 'pages.admin.users.title',
        '/admin/assignments': 'pages.admin.assignments.title',
        '/admin/sessions': 'pages.admin.sessions.title',
        '/admin/series': 'pages.admin.series.title',
        '/admin/payments': 'pages.admin.payments.title',
        '/admin/sponsors': 'pages.admin.sponsors.title',
        '/admin/branches': 'pages.admin.branches.title',
        '/admin/sections': 'pages.admin.sections.title',
        '/admin/training-groups': 'pages.admin.training_groups.title',
        '/admin/notifications/broadcast': 'pages.admin.notifications.broadcast.title',
        '/admin/facilities': 'pages.admin.facilities.title',
        '/admin/settings': 'pages.admin.settings.title',
        '/admin/injuries': 'pages.admin.injuries.title',
        '/admin/legal-guardians': 'pages.admin.legal_guardians.title',
        '/admin/players': 'pages.admin.player_profile.breadcrumb_list',
      };

      this.backRoute = '/dashboard';
      this.showBackButton = cleanUrl !== '/dashboard';
      this.breadcrumbs = this.buildBreadcrumbs(
        cleanUrl,
        'pages.admin.dashboard.title',
        '/dashboard',
        adminLabelMap
      );
      return;
    }

    const labelMap: Record<string, string> = {
      '/profile': 'pages.dashboard.cards.common.profile.title',
      '/player/profile': 'pages.profile.title',
      '/teams': 'pages.dashboard.cards.common.teams.title',
      '/messages': 'pages.dashboard.cards.common.messages.title',
      '/coach/attendance': 'pages.dashboard.cards.coach.attendance.title',
      '/coach/training': 'pages.dashboard.cards.coach.training.title',
      '/coach/sessions': 'pages.coach.sessions.title',
      '/coach/series': 'pages.coach.series.title',
      '/coach/athletes': 'pages.dashboard.cards.coach.athletes.title',
      '/parent/children': 'pages.dashboard.cards.parent.children.title',
      '/parent/payments': 'pages.dashboard.cards.parent.payments.title',
      '/parent/documents': 'pages.dashboard.cards.parent.docs.title',
      '/player/coaches': 'pages.dashboard.cards.player.coaches.title',
      '/player/schedule': 'pages.dashboard.cards.player.schedule.title',
      '/player/sessions': 'pages.player.sessions.title',
      '/player/attendance': 'pages.dashboard.cards.player.attendance.title',
      '/player/injuries': 'pages.dashboard.cards.player.injuries.title'
    };

    if (cleanUrl === '/dashboard') {
      this.backRoute = '/dashboard';
      this.showBackButton = false;
      this.breadcrumbs = [];
      return;
    }

    this.backRoute = '/dashboard';
    this.showBackButton = cleanUrl !== '/dashboard';
    this.breadcrumbs = this.buildBreadcrumbs(
      cleanUrl,
      'pages.dashboard.title',
      '/dashboard',
      labelMap
    );
  }

  private buildBreadcrumbs(
    url: string,
    rootLabelKey: string,
    rootPath: string,
    labelMap: Record<string, string>
  ): Array<{ labelKey: string; url?: string }> {
    const breadcrumbs: Array<{ labelKey: string; url?: string }> = [
      {
        labelKey: rootLabelKey,
        url: url !== rootPath ? rootPath : undefined
      }
    ];

    if (url === rootPath) {
      return breadcrumbs;
    }

    const currentLabel = labelMap[url];
    if (currentLabel) {
      breadcrumbs.push({ labelKey: currentLabel });
    }

    return breadcrumbs;
  }
}
